import { Divider } from '@/components';
import { OrgProfileVisibilitySettings, Organization } from '@/graphql/types';
import { cleanDisplayUrl, formatPhoneNumber } from '@/utils/stringUtils';
import { cx } from '@emotion/css';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import PublicIcon from '@mui/icons-material/Public';
import {
  BodyMedium,
  BodyMediumMidEmphasisUnderline,
  Tag,
  TitleSmallEmphasized,
} from '@/design-system';
import colors from '@/theme/colors';
import { capitalize } from 'lodash';
import { FollowButton } from '../FollowButton/FollowButton';
import styles from './OrgProfileInfo.styles';

interface OrgProfileInfoProps {
  org: Organization;
  visibilitySettings: OrgProfileVisibilitySettings;
  trackButtonClick: (label: string) => void;
}

export const OrgProfileInfo: React.FC<OrgProfileInfoProps> = ({
  org,
  visibilitySettings,
  trackButtonClick,
}) => {
  const {
    showPhone,
    showContactEmail,
    showFullAddress,
    showYearEstablished,
    showChildEnrichmentStatement,
    showWebsite,
    showSocialMedia,
    showSeasonality,
  } = visibilitySettings;

  const showYearEstablishedTag = Boolean(showYearEstablished && org.yearEstablished);
  const showChildEnrichmentStatementSection = Boolean(
    showChildEnrichmentStatement && org.childEnrichmentStatement
  );

  return (
    <>
      <div className={styles.section}>
        <div className={styles.orgNameTitle}>
          <TitleSmallEmphasized>{org.name}</TitleSmallEmphasized>
          <FollowButton
            orgId={Number(org.orgId)}
            orgName={org.name}
            onClick={() => trackButtonClick('Follow')}
            isFollowingInitial={org.isFollowing}
          />
        </div>
        <div
          className={cx(
            showYearEstablishedTag && showChildEnrichmentStatementSection && styles.subheadingInfo
          )}
        >
          {showYearEstablishedTag && (
            <div className={styles.tagsContainer}>
              <Tag variant="timeline">Since {org.yearEstablished}</Tag>
            </div>
          )}
          {showChildEnrichmentStatementSection && (
            <BodyMedium>{org.childEnrichmentStatement}</BodyMedium>
          )}
        </div>
        <Divider className={styles.divider} />
      </div>
      <div className={cx(styles.section, styles.infoContainer)}>
        {showPhone && (
          <div className={styles.info}>
            <PhoneInTalkOutlinedIcon />
            <a
              href={`tel:${org.links.call}`}
              className={styles.infoText}
              aria-label="organization phone"
              onClick={() => {
                trackButtonClick('Call');
              }}
            >
              <BodyMedium>{formatPhoneNumber(org.links.call)}</BodyMedium>
            </a>
          </div>
        )}
        {showContactEmail && (
          <div className={styles.info}>
            <EmailOutlinedIcon />
            <a
              href={`mailto:${org.links.email}`}
              className={styles.infoText}
              aria-label="organization email"
              onClick={() => {
                trackButtonClick('Email');
              }}
            >
              <BodyMediumMidEmphasisUnderline color={colors.jungle}>
                {org.links.email}
              </BodyMediumMidEmphasisUnderline>
            </a>
          </div>
        )}
        {showWebsite && org.links.website && (
          <div className={styles.info}>
            <PublicIcon />
            <a
              href={`${org.links.website}`}
              className={styles.infoText}
              target="_blank"
              rel="noreferrer"
              aria-label="organization website"
              onClick={() => {
                trackButtonClick('Website');
              }}
            >
              <BodyMediumMidEmphasisUnderline color={colors.jungle}>
                {cleanDisplayUrl(org.links.website)}
              </BodyMediumMidEmphasisUnderline>
            </a>
          </div>
        )}
        {(org.mapUrl || org.address) && (
          <div className={styles.info}>
            <LocationOnOutlinedIcon />
            <BodyMedium className={styles.infoText}>
              {showFullAddress ? org.mapUrl : org.address} <br />
              {showFullAddress && org.mapUrl && (
                <a
                  href={`http://maps.google.com/?daddr=${org.mapUrl}`}
                  target="_blank"
                  className={styles.link}
                  rel="noreferrer"
                  aria-label="organization address"
                  onClick={() => {
                    trackButtonClick('Address');
                  }}
                >
                  <BodyMediumMidEmphasisUnderline color={colors.jungle}>
                    View Location
                  </BodyMediumMidEmphasisUnderline>
                </a>
              )}
            </BodyMedium>
          </div>
        )}
        {showSocialMedia && org?.socialMediaUrl && (
          <div className={styles.info}>
            <ShareOutlinedIcon />
            <a
              href={`${org.socialMediaUrl}`}
              className={styles.infoText}
              target="_blank"
              rel="noreferrer"
              aria-label="organization social media"
              onClick={() => {
                trackButtonClick('Social Media');
              }}
            >
              <BodyMediumMidEmphasisUnderline color={colors.jungle}>
                {cleanDisplayUrl(org.socialMediaUrl)}
              </BodyMediumMidEmphasisUnderline>
            </a>
          </div>
        )}
        {showSeasonality && org.seasonality?.length > 0 && (
          <div className={styles.info}>
            <BeachAccessOutlinedIcon />
            <BodyMedium className={styles.infoText}>
              {org.seasonality.map(capitalize).join(', ')}
            </BodyMedium>
          </div>
        )}
      </div>
    </>
  );
};
